import React, { useEffect, useMemo } from "react";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createInputValidator } from "../validation/defaultValidators";

export interface InputFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
    label: string;
    name: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const InputField: React.FC<InputFieldProps> = ({
    label,
    name,
    type = "text",
    className,
    placeholder,
    required = false,
    validate,
    validationMessage,
    ...inputProps
}) => {
    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createInputValidator(type, required),
        [required, type, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [effectiveValidator, name, registerFieldValidation, validationMessage]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        clearFieldError(name);
        inputProps.onChange?.(event);
    };

    return (
        <div className="form-field">
            <label htmlFor={name}>{label}</label>

            <span className="wpcf7-form-control-wrap" data-name={name}>
                <input
                    {...inputProps}
                    id={name}
                    name={name}
                    type={type}
                    required={required}
                    placeholder={placeholder}
                    onChange={handleChange}
                    className={[
                        "input",
                        `input--${type}`,
                        fieldError ? "wpcf7-not-valid" : null,
                        className,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                />

                {fieldError ? (
                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                        {fieldError}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

